import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import type { MessageHistogramBucket } from '@/types/attendance-metrics'

interface MessageHistogramChartProps {
  data: MessageHistogramBucket[]
  interval?: 'hour' | 'day'
  isLoading?: boolean
  title?: string
  description?: string
}

function formatTimestamp(timestamp: string, interval: 'hour' | 'day'): string {
  const date = new Date(timestamp)
  if (interval === 'hour') {
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  })
}

export function MessageHistogramChart({
  data,
  interval = 'day',
  isLoading,
  title = 'Volume de Mensagens',
  description = 'Mensagens recebidas (clientes) e enviadas (atendentes) ao longo do tempo',
}: MessageHistogramChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[200px] md:h-[300px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] md:h-[300px] flex items-center justify-center">
            <p className="text-sm text-muted-foreground">Sem dados para o período selecionado</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Transform data for chart
  const chartData = data.map((bucket) => ({
    ...bucket,
    label: formatTimestamp(bucket.timestamp, interval),
  }))

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[200px] md:h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" opacity={0.3} />
              <XAxis
                dataKey="label"
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                interval="preserveStartEnd"
                tickMargin={8}
              />
              <YAxis
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                }}
                formatter={(value: number | undefined, name: string | undefined) => [
                  (value ?? 0).toLocaleString(),
                  name === 'inbound' ? 'Recebidas' : 'Enviadas',
                ]}
                labelFormatter={(label) => `Período: ${label}`}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
              />
              <Legend
                formatter={(value) => (value === 'inbound' ? 'Recebidas (Clientes)' : 'Enviadas (Atendentes)')}
              />
              <Bar
                dataKey="inbound"
                fill="hsl(var(--primary))"
                radius={[4, 4, 0, 0]}
                name="inbound"
              />
              <Bar
                dataKey="outbound"
                fill="hsl(var(--chart-2))"
                radius={[4, 4, 0, 0]}
                name="outbound"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
